import { ArrowLeft } from '@liveauctioneers/hammer-ui-core/icons';
import { InlineButton } from '@liveauctioneers/hammer-ui-core/inlineButton';
import { useIntl } from '@liveauctioneers/hammer-ui-core/intl';
import { SubtleMonochromeLink } from '@liveauctioneers/hammer-ui-core/link';
import { BodyPrimary } from '@liveauctioneers/hammer-ui-core/text';
import { MouseEvent, useCallback } from 'react';
import { useLocation } from 'react-router-dom';

import { useNavigateSearchPageWithParams } from '@/pages/Search/SearchPage/hooks/useNavigateSearchPageWithParams';
import { CurrentOpenMenu } from '@/pages/Search/SearchPage/types';
import { useAppSelector } from '@/redux/hooks';
import { getSelectedCategoryFacetValue } from '@/redux/modules/searchFacetAggregates.selectors';
import FacetTypes from '@/types/search/enums/facetTypes';
import paramTypes from '@/types/search/enums/paramTypes';
import { FilterAction, ResultFacet } from '@/types/SearchFacet';
import { getCategoryBrowseNavigateUrl } from '@/utils/browsePath/buildCategoryBrowseNavigateUrl';
import {
    addValueToParamsList,
    modifyParamsRemovePage,
    modifyParamsWithTaxonomyAll,
    modifyParamsWithTaxonomySelected,
    parseQueryParams,
} from '@/utils/queryParams';
import { getSearchUrlWithParams, isCategoryPage, isPriceGuidePage } from '@/utils/urls';

type Props = {
    currentOpenMenu: CurrentOpenMenu;
    isAllFilters: boolean;
    facets: ResultFacet[];
};
export const CategoryBackLinks = ({ currentOpenMenu, isAllFilters, facets }: Props) => {
    const { pathname, search } = useLocation();
    const queryParams = parseQueryParams(search);
    const isOnBrowseCategoryPage = isCategoryPage(pathname) || isPriceGuidePage(pathname);
    const currentCategoryId = queryParams[paramTypes.CATEGORY_ID]?.toString();
    const navigateWithParams = useNavigateSearchPageWithParams();
    const { formatMessage } = useIntl();

    const nestedCategoryFacet = useAppSelector((state) => getSelectedCategoryFacetValue(state, FacetTypes.CATEGORY_ID));

    const buildCategoryBackLinkParams = useCallback(
        (categoryId: string, remove?: boolean) => {
            let newParams = modifyParamsRemovePage(queryParams);
            if (remove) {
                delete newParams[paramTypes.CATEGORY_ID];
                newParams = modifyParamsWithTaxonomyAll(newParams, FacetTypes.CATEGORY_ID, categoryId);
            } else if (currentCategoryId?.includes(',')) {
                newParams = modifyParamsWithTaxonomySelected(newParams, FacetTypes.CATEGORY_ID, categoryId, facets);
            } else {
                delete newParams[paramTypes.CATEGORY_ID];
                newParams = addValueToParamsList(newParams, paramTypes.CATEGORY_ID, categoryId);
            }

            return newParams;
        },
        [currentCategoryId, queryParams, facets]
    );

    const buildCategoryBackLinkUrl = useCallback(
        (params: ReturnType<typeof buildCategoryBackLinkParams>) =>
            isOnBrowseCategoryPage
                ? getCategoryBrowseNavigateUrl(pathname, params, facets)
                : getSearchUrlWithParams(params),
        [facets, isOnBrowseCategoryPage, pathname]
    );

    const handleNavigate = (event: MouseEvent<HTMLAnchorElement>, params: any) => {
        event.preventDefault();

        navigateWithParams({
            checkForPageRemoval: true,
            currentOpenMenu: isAllFilters ? CurrentOpenMenu.AllFilters : currentOpenMenu,
            filterAction: params.category_id ? FilterAction.CategoryChanged : FilterAction.CategoryRemoved,
            params,
            preventScrollReset: true,
        });
    };

    return (
        <>
            {currentCategoryId && (
                <SubtleMonochromeLink
                    href={buildCategoryBackLinkUrl(buildCategoryBackLinkParams(currentCategoryId, true))}
                    onClick={(event) => handleNavigate(event, buildCategoryBackLinkParams(currentCategoryId, true))}
                >
                    <InlineButton
                        icon={ArrowLeft}
                        style="neutral"
                    >
                        Back to all {formatMessage({ id: 'search_facet_title_F1' })}
                    </InlineButton>
                </SubtleMonochromeLink>
            )}
            {nestedCategoryFacet.parents && (
                <>
                    {nestedCategoryFacet.parents.map((parent) => (
                        <SubtleMonochromeLink
                            className="group grid grid-cols-[20px_1fr] items-center gap-2xs"
                            href={buildCategoryBackLinkUrl(buildCategoryBackLinkParams(parent.id))}
                            key={parent.id}
                            onClick={(event) => handleNavigate(event, buildCategoryBackLinkParams(parent.id))}
                        >
                            <ArrowLeft
                                className="text-text-secondary group-hover:text-surface-900"
                                size="sm"
                            />
                            <BodyPrimary
                                className="group-hover:text-surface-900"
                                color="secondary"
                            >
                                Back to {parent.name}
                            </BodyPrimary>
                        </SubtleMonochromeLink>
                    ))}
                </>
            )}
        </>
    );
};
